import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import './OnboardingTour.css';

/** Bump when tour copy or steps change so users see the new tour once */
export const TOUR_STORAGE_KEY = 'deckpack-tour-v11';

const STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Deck Pack',
    body:
      'Deck Pack turns Google Drive folders into playlists of slide decks—so you can run worship sets, lesson blocks, or any multi-deck flow from one control panel.\n\n' +
      'Spotted a bug or have feedback? Email korthmichael@gmail.com (also listed on our Privacy policy).',
    selector: null,
  },
  {
    id: 'packs',
    title: 'Packs',
    body:
      'A Pack is a Drive folder. Create one, link an existing folder, or pick from your list here. Each pack holds the decks you’ll play in order.',
    selector: '[data-tour="tour-packs"]',
  },
  {
    id: 'playlist',
    title: 'Playlist',
    body:
      'Add Google Slides files from Drive, drag to reorder, and use Auto-advance to jump to the next deck when you reach the last slide. If you want to add an image or video to the playlist, create a separate slide deck or add the image or video to a slide within your presentation.',
    selector: '[data-tour="tour-playlist"]',
    missingHint:
      'Open or create a pack to see the playlist. You’ll add decks here and set the order for your session.',
  },
  {
    id: 'remote',
    title: 'Phone / tablet remote',
    body:
      'Start a remote session from here so another device can control slides, timer, and blackout. You approve who connects; the remote shows notes and thumbnails like a handheld clicker with context.',
    selector: '[data-tour="tour-remote"]',
    missingHint:
      'Open a pack to see the remote button next to Auto-advance in the playlist footer.',
  },
  {
    id: 'preview',
    title: 'Slide preview',
    body:
      'The live preview follows the slide you select (or keyboard arrows). The frame is locked so navigation stays in sync with your playlist and presenter view.',
    selector: '[data-tour="tour-preview"]',
    missingHint:
      'When a pack is open and a deck is active, the main preview appears here.',
  },
  {
    id: 'notes',
    title: 'Speaker notes',
    body:
      'Speaker notes for the current slide show below the preview. Drag the divider to give more room to notes or the slide.',
    selector: '[data-tour="tour-notes"]',
    missingHint:
      'Once a deck is active, your notes appear in this area under the preview.',
  },
  {
    id: 'thumbs',
    title: 'Slides & thumbnails',
    body:
      'Jump to any slide with one click. Thumbnails load from Google Slides so you can see where you are in the deck at a glance.',
    selector: '[data-tour="tour-slides-strip"]',
  },
  {
    id: 'slide-shortcuts',
    title: 'Slide shortcuts',
    body:
      'Click the keyboard icon, press a letter or number, and that key jumps to the slide. The badge shows the key; Backspace clears it while assigning.',
    selector: '[data-tour="tour-slide-shortcut-spotlight"]',
    missingHint: 'Open a pack, pick a deck, and wait for thumbnails so the highlighted slide appears.',
  },
  {
    id: 'views',
    title: 'Audience & Presenter',
    body:
      'Open Audience View for the full-window slideshow and Presenter View for a separate window with slide views, notes, and a timer - perfect for a confidence monitor.',
    selector: '[data-tour="tour-open-views"]',
  },
  {
    id: 'controls',
    title: 'Stage controls',
    body:
      'Timer reset talks to the presenter window. Blackout and Whiteout send overlays to the audience view—handy for pauses or transitions.',
    selector: '[data-tour="tour-stage-controls"]',
  },
];

function getRect(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 && r.height < 2) return null;
  return {
    left: r.left,
    top: r.top,
    width: r.width,
    height: r.height,
  };
}

const MARGIN = 16;
const GAP = 14; // min gap between highlight and tooltip card

/** True if two axis-aligned rects overlap */
function rectsOverlap(a, b) {
  return !(
    a.left + a.width <= b.left ||
    b.left + b.width <= a.left ||
    a.top + a.height <= b.top ||
    b.top + b.height <= a.top
  );
}

/**
 * Place tooltip so it does not cover the target region. Tries right → left → below → above
 * of the highlight, then safe viewport corners.
 */
function computeCardPlacement(targetRect, cardW, cardH) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const avoid = {
    left: targetRect.left - GAP,
    top: targetRect.top - GAP,
    width: targetRect.width + GAP * 2,
    height: targetRect.height + GAP * 2,
  };

  const clampX = (x, w = cardW) => Math.max(MARGIN, Math.min(x, vw - w - MARGIN));
  const clampY = (y) => Math.max(MARGIN, Math.min(y, vh - cardH - MARGIN));

  const tryCard = (left, top, w = cardW) => {
    const card = { left, top, width: w, height: cardH };
    if (rectsOverlap(card, avoid)) return null;
    if (left < MARGIN || top < MARGIN || left + w > vw - MARGIN || top + cardH > vh - MARGIN) {
      return null;
    }
    return { left, top, width: w };
  };

  const cx = avoid.left + avoid.width / 2;

  // 1) To the right of target (works for left sidebar)
  let placed = tryCard(avoid.left + avoid.width + GAP, clampY(avoid.top + avoid.height / 2 - cardH / 2));
  if (placed) return placed;

  // 2) To the left of target (works for right sidebar / slides)
  placed = tryCard(avoid.left - cardW - GAP, clampY(avoid.top + avoid.height / 2 - cardH / 2));
  if (placed) return placed;

  // 3) Below target (wide center preview / notes)
  placed = tryCard(clampX(cx - cardW / 2), avoid.top + avoid.height + GAP);
  if (placed) return placed;

  // 4) Above target
  placed = tryCard(clampX(cx - cardW / 2), avoid.top - cardH - GAP);
  if (placed) return placed;

  // 5) Bottom edge band (full width), only if it clears target vertically
  placed = tryCard(clampX(cx - cardW / 2), vh - cardH - MARGIN);
  if (placed) return placed;

  // 6) Top edge band
  placed = tryCard(clampX(cx - cardW / 2), MARGIN);
  if (placed) return placed;

  // 7) Screen corners (often clear when the target fills the middle)
  const cornerPositions = [
    [MARGIN, MARGIN],
    [vw - cardW - MARGIN, MARGIN],
    [MARGIN, vh - cardH - MARGIN],
    [vw - cardW - MARGIN, vh - cardH - MARGIN],
  ];
  for (const [cl, ct] of cornerPositions) {
    placed = tryCard(cl, ct);
    if (placed) return placed;
  }

  // 8) Side with more horizontal space
  const spaceRight = vw - (avoid.left + avoid.width);
  const spaceLeft = avoid.left;
  if (spaceRight >= spaceLeft && spaceRight >= cardW + MARGIN * 2) {
    placed = tryCard(vw - cardW - MARGIN, MARGIN);
    if (placed) return placed;
  }
  if (spaceLeft >= cardW + MARGIN * 2) {
    placed = tryCard(MARGIN, MARGIN);
    if (placed) return placed;
  }

  // Last resort: shrink width and pin top-right
  const narrowW = Math.min(cardW, 300);
  placed = tryCard(vw - narrowW - MARGIN, MARGIN, narrowW);
  if (placed) return placed;

  return { left: MARGIN, top: MARGIN, width: narrowW };
}

export function hasCompletedTour() {
  try {
    return localStorage.getItem(TOUR_STORAGE_KEY) === '1';
  } catch {
    return true;
  }
}

export function markTourCompleted() {
  try {
    localStorage.setItem(TOUR_STORAGE_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * @param {{ active: boolean, onDismiss: () => void }} props
 */
export default function OnboardingTour({ active, onDismiss }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [centered, setCentered] = useState(true);
  const [viewportWidth, setViewportWidth] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth : 1024)
  );
  const cardRef = useRef(null);

  const step = STEPS[stepIndex];
  const total = STEPS.length;

  const updatePosition = useCallback(() => {
    if (!active || !step) return;
    if (!step.selector) {
      setRect(null);
      setCentered(true);
      return;
    }
    const el = document.querySelector(step.selector);
    const r = getRect(el);
    if (r) {
      setRect(r);
      setCentered(false);
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } else {
      setRect(null);
      setCentered(true);
    }
  }, [active, step]);

  useEffect(() => {
    if (!active) {
      setStepIndex(0);
      return;
    }
    updatePosition();
    // Flex layout for tour placeholders often measures 0x0 on the first paint; re-measure after layout.
    const retryMs = [32, 96, 200, 360];
    const timers = retryMs.map((ms) => setTimeout(updatePosition, ms));
    return () => timers.forEach(clearTimeout);
  }, [active, stepIndex, updatePosition]);

  useEffect(() => {
    if (!active) return;
    const onResize = () => {
      setViewportWidth(window.innerWidth);
      updatePosition();
    };
    window.addEventListener('resize', onResize);
    const t = setTimeout(updatePosition, 120);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, [active, updatePosition]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        markTourCompleted();
        onDismiss();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, onDismiss]);

  const CARD_W = Math.min(380, viewportWidth - 32);
  const CARD_H = 268; // keep aligned with .onboarding-tour-card--anchored max-height

  const anchoredCardStyle = useMemo(() => {
    if (!rect || centered) return null;
    return computeCardPlacement(rect, CARD_W, CARD_H);
  }, [rect, centered, CARD_W, CARD_H]);

  const goNext = () => {
    if (stepIndex < total - 1) setStepIndex((i) => i + 1);
    else {
      markTourCompleted();
      onDismiss();
    }
  };

  const goBack = () => {
    if (stepIndex > 0) setStepIndex((i) => i - 1);
  };

  const skip = () => {
    markTourCompleted();
    onDismiss();
  };

  if (!active) return null;

  const bodyText =
    centered && step.selector && step.missingHint ? `${step.body} ${step.missingHint}` : step.body;

  const isLast = stepIndex === total - 1;

  const holePad = 6;
  const clipPathHole =
    rect && !centered
      ? (() => {
          const l = rect.left - holePad;
          const t = rect.top - holePad;
          const r = rect.left + rect.width + holePad;
          const b = rect.top + rect.height + holePad;
          return `polygon(evenodd, 0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%, ${l}px ${t}px, ${l}px ${b}px, ${r}px ${b}px, ${r}px ${t}px, ${l}px ${t}px)`;
        })()
      : null;

  return (
    <div className="onboarding-tour-root" role="dialog" aria-modal="true" aria-labelledby="tour-step-title">
      {rect && !centered && clipPathHole ? (
        <>
          <div
            className="onboarding-tour-dim"
            style={{ clipPath: clipPathHole, WebkitClipPath: clipPathHole }}
            aria-hidden="true"
          />
          <div
            className="onboarding-tour-highlight-ring"
            style={{
              left: rect.left - holePad,
              top: rect.top - holePad,
              width: rect.width + holePad * 2,
              height: rect.height + holePad * 2,
            }}
            aria-hidden="true"
          />
        </>
      ) : (
        <div className="onboarding-tour-backdrop-full" aria-hidden="true" />
      )}

      <div
        ref={cardRef}
        className={`onboarding-tour-card ${centered ? 'onboarding-tour-card--center' : 'onboarding-tour-card--anchored'}`}
        style={
          !centered && anchoredCardStyle
            ? {
                top: anchoredCardStyle.top,
                left: anchoredCardStyle.left,
                width: anchoredCardStyle.width,
              }
            : undefined
        }
      >
        <p className="onboarding-tour-progress">
          Step {stepIndex + 1} of {total}
        </p>
        <h2 id="tour-step-title" className="onboarding-tour-title">
          {step.title}
        </h2>
        <p className="onboarding-tour-body">{bodyText}</p>
        <div className="onboarding-tour-actions">
          <button type="button" className="onboarding-tour-btn onboarding-tour-btn--ghost" onClick={skip}>
            Skip tour
          </button>
          <div className="onboarding-tour-nav">
            <button
              type="button"
              className="onboarding-tour-btn onboarding-tour-btn--secondary"
              onClick={goBack}
              disabled={stepIndex === 0}
            >
              Back
            </button>
            <button type="button" className="onboarding-tour-btn onboarding-tour-btn--primary" onClick={goNext}>
              {isLast ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
